import { useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { ControllerFontType, makeStyles } from '@bymason-private/controller-v1-theme-provider';
import { ArtifactType, BuildResult, BuildStatus, useGetConfigArtifact } from '@bymason/controller-api-hooks';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Text,
  FaChevronDown,
  ControllerAlert,
  Link,
} from '@bymason-private/legacy-pattern-components';
import { useToast, ArtifactTypeIcon } from '@bymason-private/controller-shared-widgets';
import { formatLongDateTimeString } from '@bymason/utils';
import { ConfigReview, ConfigReviewProps } from '../config-review';

const useStyles = makeStyles((theme) => ({
  configSection: {
    padding: '8px 16px',
    margin: 0,
    marginBottom: 16,
    border: '1px solid ' + theme.palette.grey['200'],
  },
  summaryRoot: {
    padding: 0,
    '&&:hover': {
      backgroundColor: theme.palette.background.default,
      cursor: 'pointer',
    },
    '& MuiAccordionSummary-content': {
      justifyContent: 'space-between',
    },
  },
  detailRoot: {
    padding: 0,
    paddingTop: 16,
  },
  headingContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '& > *': {
      marginRight: '8px',
    },
  },
  heading: {
    lineHeightPx: 24,
  },
  subHeading: {
    color: theme.palette.grey['600'],
  },
  chip: {
    marginLeft: 'auto',
    '& > *': {
      marginLeft: 4,
      padding: '3px 8px',
    },
    '& > .info': {
      backgroundColor: theme.palette.grey['50'],
    },
    '& > .error': {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.error.main,
    },
    '& > .success': {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.dark,
    },
    '& > .warn': {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.main,
    },
  },
}));

export const BuildReview = ({
  projectName,
  version,
  createdAt,
  buildResult,
  buildStatus,
  defaultExpanded,
}: BuildReviewProps) => {
  const { authToken = '', customerId } = useAuth();
  const classes = useStyles();
  const [setToastMessage, setToastSeverity] = useToast();
  const [getConfig, configArtifact, configError, configBusy] = useGetConfigArtifact(authToken);

  const handleGetConfig = useCallback(() => {
    if (customerId && projectName && version && !configArtifact) {
      getConfig({ customerId, name: projectName, version });
    }
  }, [configArtifact, customerId, getConfig, projectName, version]);

  const configJson = useMemo(() => {
    return configArtifact?.config;
  }, [configArtifact?.config]);

  useEffect(() => {
    if (customerId && configJson) {
      const optionsList = (configJson?.apps || []).map((app) => {
        const { name, version_code: version } = app;
        return { customerId, type: ArtifactType.Apk, name: name, version: version };
      });
      if (configJson.media?.splash) {
        const { name, version } = configJson.media.splash;
        optionsList.push({ customerId, type: ArtifactType.Media, name: name, version: version });
      }
      if (configJson.media?.bootanimation) {
        const { name, version } = configJson.media.bootanimation;
        optionsList.push({ customerId, type: ArtifactType.Media, name: name, version: version });
      }
    }
  }, [customerId, configJson]);

  useEffect(() => {
    if (defaultExpanded) {
      handleGetConfig();
    }
  }, [defaultExpanded, handleGetConfig]);

  useEffect(() => {
    const errorMessage = configError || '';
    if (errorMessage) {
      setToastMessage(errorMessage);
      setToastSeverity('error');
    }
  }, [configError, setToastMessage, setToastSeverity]);

  return (
    <Accordion
      id={version}
      className={classes.configSection}
      disableGutters
      defaultExpanded={defaultExpanded}
      onClick={handleGetConfig}
    >
      <AccordionSummary
        className={classes.summaryRoot}
        expandIcon={<FaChevronDown />}
        sx={{
          '& .MuiAccordionSummary-expandIconWrapper': {
            marginRight: '8px',
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
          },
        }}
      >
        <div className={classes.headingContainer}>
          <ArtifactTypeIcon type={ArtifactType.Config} fontSize={20} />
          <div>
            <Text className={classes.heading} variant={ControllerFontType.Text500M} component='div'>
              {projectName}
            </Text>
            <Text className={classes.subHeading} variant={ControllerFontType.Heading250M} component='div'>
              {createdAt ? formatLongDateTimeString(createdAt) : ''}
            </Text>
          </div>
          <div className={classes.chip}>
            {version && (
              <Text className='info' variant={ControllerFontType.Body300R} component='span'>
                {`Version: ${version}`}
              </Text>
            )}
            {buildStatus === BuildStatus.Completed && buildResult === BuildResult.Failed && (
              <Text className='error' variant={ControllerFontType.Body300R} component='span'>
                {`Failed`}
              </Text>
            )}
            {buildStatus === BuildStatus.Completed && buildResult === BuildResult.Success && (
              <Text className='success' variant={ControllerFontType.Body300R} component='span'>
                {`Success`}
              </Text>
            )}
            {(buildStatus === BuildStatus.Created ||
              buildStatus === BuildStatus.Started ||
              buildResult === BuildResult.Pending) && (
              <Text className='warn' variant={ControllerFontType.Body300R} component='span'>
                {`Pending...`}
              </Text>
            )}
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.detailRoot}>
        {!configBusy && configJson && (
          <ConfigReview
            config={configJson}
            projectName={projectName}
            otherAlert={buildResult === BuildResult.Failed ? <BuildFailureAlert /> : null}
          />
        )}
        {!configBusy &&
          !configJson &&
          (configError ? (
            <ControllerAlert
              sx={{
                width: '100%',
                marginBottom: '8px',
              }}
              variant='standard'
              severity='warning'
            >
              There was a problem retrieving the config
            </ControllerAlert>
          ) : (
            <ControllerAlert
              sx={{
                width: '100%',
                marginBottom: '8px',
              }}
              variant='standard'
              severity='info'
            >
              No config
            </ControllerAlert>
          ))}
      </AccordionDetails>
    </Accordion>
  );
};

interface BuildReviewProps extends Omit<ConfigReviewProps, 'config'> {
  projectName: string;
  version: string;
  createdAt?: string;
  buildResult?: BuildResult;
  buildStatus?: BuildStatus;
  defaultExpanded?: boolean;
}

// Alert components

const BuildFailureAlert = () => {
  return (
    <ControllerAlert
      sx={{
        width: '100%',
        marginBottom: '8px',
      }}
      variant='outlined'
      severity='error'
    >
      <div>
        <span>
          Please verify all apps and media in your Project Configuration have been registered. See{' '}
          <Link
            href='https://docs.bymason.com/mason-os/overview#example-mason-os-configuration'
            target='_blank'
            rel='noopener noreferrer'
          >
            Mason Documentation
          </Link>{' '}
          or contact{' '}
          <Link href='mailto:support@bymason.com' target='_blank' rel='noopener noreferrer'>
            support@bymason.com
          </Link>{' '}
          for more information.
        </span>
      </div>
    </ControllerAlert>
  );
};
